import { isAxiosError } from 'axios';
import type { AxiosError } from 'axios';
import type { ApiError } from '../../types/auth';

/**
 * API error handling utilities
 */

// Common error codes from FastAPI Users
export const AUTH_ERROR_CODES = {
  LOGIN_BAD_CREDENTIALS: 'LOGIN_BAD_CREDENTIALS',
  LOGIN_USER_NOT_VERIFIED: 'LOGIN_USER_NOT_VERIFIED',
  REGISTER_USER_ALREADY_EXISTS: 'REGISTER_USER_ALREADY_EXISTS',
  REGISTER_INVALID_PASSWORD: 'REGISTER_INVALID_PASSWORD',
  RESET_PASSWORD_BAD_TOKEN: 'RESET_PASSWORD_BAD_TOKEN',
  RESET_PASSWORD_INVALID_PASSWORD: 'RESET_PASSWORD_INVALID_PASSWORD',
  VERIFY_USER_BAD_TOKEN: 'VERIFY_USER_BAD_TOKEN',
  VERIFY_USER_ALREADY_VERIFIED: 'VERIFY_USER_ALREADY_VERIFIED',
  UPDATE_USER_EMAIL_ALREADY_EXISTS: 'UPDATE_USER_EMAIL_ALREADY_EXISTS',
  UPDATE_USER_INVALID_PASSWORD: 'UPDATE_USER_INVALID_PASSWORD',
  // Disabled-account variants. Keycloak's enabled flag is binary; the
  // backend reads UserProfile.AccountStatus to pick the right code:
  //   pending_approval → waitlist UI
  //   banned           → ToS-violation message, no recovery path
  //   suspended        → billing/ops freeze, link to billing page
  //   deactivated      → user-initiated, contact-support
  //   (unknown)        → generic disabled
  LOGIN_PENDING_APPROVAL: 'LOGIN_PENDING_APPROVAL',
  LOGIN_ACCOUNT_BANNED: 'LOGIN_ACCOUNT_BANNED',
  LOGIN_ACCOUNT_SUSPENDED: 'LOGIN_ACCOUNT_SUSPENDED',
  LOGIN_ACCOUNT_DEACTIVATED: 'LOGIN_ACCOUNT_DEACTIVATED',
  LOGIN_ACCOUNT_DISABLED: 'LOGIN_ACCOUNT_DISABLED',
  // Re-register attempts on existing accounts in non-active status. Each
  // gets its own banner so the user understands what to do (waitlist for
  // pending; support contact for banned/suspended/deactivated).
  REGISTER_PENDING_APPROVAL: 'REGISTER_PENDING_APPROVAL',
  REGISTER_ACCOUNT_BANNED: 'REGISTER_ACCOUNT_BANNED',
  REGISTER_ACCOUNT_SUSPENDED: 'REGISTER_ACCOUNT_SUSPENDED',
  REGISTER_ACCOUNT_DEACTIVATED: 'REGISTER_ACCOUNT_DEACTIVATED',
} as const;

// User-friendly error messages
export const ERROR_MESSAGES: Record<string, string> = {
  [AUTH_ERROR_CODES.LOGIN_BAD_CREDENTIALS]: 'Invalid email or password',
  [AUTH_ERROR_CODES.LOGIN_USER_NOT_VERIFIED]: 'Please verify your email address before logging in',
  [AUTH_ERROR_CODES.REGISTER_USER_ALREADY_EXISTS]: 'An account with this email already exists',
  [AUTH_ERROR_CODES.REGISTER_INVALID_PASSWORD]: 'Password does not meet requirements',
  [AUTH_ERROR_CODES.RESET_PASSWORD_BAD_TOKEN]: 'Invalid or expired reset token',
  [AUTH_ERROR_CODES.RESET_PASSWORD_INVALID_PASSWORD]: 'Password does not meet requirements',
  [AUTH_ERROR_CODES.VERIFY_USER_BAD_TOKEN]: 'Invalid or expired verification token',
  [AUTH_ERROR_CODES.VERIFY_USER_ALREADY_VERIFIED]: 'Email address is already verified',
  [AUTH_ERROR_CODES.UPDATE_USER_EMAIL_ALREADY_EXISTS]: 'This email address is already in use',
  [AUTH_ERROR_CODES.UPDATE_USER_INVALID_PASSWORD]: 'Password does not meet requirements',
  [AUTH_ERROR_CODES.LOGIN_PENDING_APPROVAL]:
    "Your account is pending approval. We'll email you once it's enabled.",
  [AUTH_ERROR_CODES.LOGIN_ACCOUNT_BANNED]:
    'This account has been suspended. Contact support if you believe this is in error.',
  [AUTH_ERROR_CODES.LOGIN_ACCOUNT_SUSPENDED]:
    'This account is suspended. Resolve outstanding billing or contact support to restore access.',
  [AUTH_ERROR_CODES.LOGIN_ACCOUNT_DEACTIVATED]:
    'This account has been deactivated. Contact support to reactivate.',
  [AUTH_ERROR_CODES.LOGIN_ACCOUNT_DISABLED]:
    'This account is currently unavailable. Contact support for help.',
  [AUTH_ERROR_CODES.REGISTER_PENDING_APPROVAL]:
    "You've already signed up — your account is still pending approval.",
  [AUTH_ERROR_CODES.REGISTER_ACCOUNT_BANNED]:
    'This email is associated with a suspended account. Contact support if you believe this is in error.',
  [AUTH_ERROR_CODES.REGISTER_ACCOUNT_SUSPENDED]:
    'This email is associated with a suspended account. Resolve outstanding billing or contact support.',
  [AUTH_ERROR_CODES.REGISTER_ACCOUNT_DEACTIVATED]:
    'This email is associated with a deactivated account. Contact support to reactivate.',
};

/**
 * Extract error message from API error response
 */
export function getErrorMessage(error: AxiosError, fallback = 'An error occurred'): string {
  const responseData = error.response?.data as ApiError | undefined;

  // Map BE detail code → canned FE copy. We never surface BE-supplied
  // text directly — the FE owns all user-visible messaging so wording is
  // consistent and we don't leak internal phrasing/PII.
  if (typeof responseData?.detail === 'string') {
    return ERROR_MESSAGES[responseData.detail] || fallback;
  }
  if (typeof responseData?.detail === 'object' && responseData.detail.code) {
    return ERROR_MESSAGES[responseData.detail.code] || fallback;
  }

  // Handle validation errors
  if (error.response?.status === 422) {
    return 'Please check your input and try again';
  }

  // Handle network errors
  if (!error.response) {
    return 'Network error. Please check your connection and try again.';
  }

  // Handle other HTTP errors
  switch (error.response.status) {
    case 400:
      return 'Bad request. Please check your input.';
    case 401:
      return 'Authentication required. Please log in.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'Server error. Please try again later.';
    default:
      return fallback;
  }
}

/**
 * Turns anything thrown into something a user should read.
 *
 * Use this at every catch site that displays a message. It exists because the
 * obvious hand-written version is wrong in a way that looks right
 *
 *     showError(err instanceof Error ? err.message : 'Failed to load memories')
 *
 * An AxiosError *is* an Error, so that fallback is unreachable and every API
 * failure showed the user `err.message` — which axios sets to "Request failed
 * with status code 500". The friendly copy only ever appeared for a non-Error
 * throw, which essentially never happens.
 *
 * Exception text is never shown, for any error type. For an Axios error the
 * message is technical; for anything else it is worse ("Cannot read properties
 * of undefined"), and for a backend error it may carry internal phrasing or PII
 * — which is the rule `getErrorMessage` already states and this extends to the
 * paths that were bypassing it.
 *
 * `fallback` is the surface's own copy, used when the error carries no code we
 * map. Make it say what failed ("Failed to load memories"), not what went wrong.
 */
export function toUserMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (isAxiosError(error)) return getErrorMessage(error, fallback);
  return fallback;
}

/**
 * Our copy for a backend detail *code*, or null when there is nothing to map.
 *
 * For callers that already have better per-situation copy than the generic
 * per-status text and only want the mapped message when one genuinely exists:
 *
 *     message: mappedMessageForCode(err) ?? `The requested ${thing} was not found.`
 *
 * Returning null rather than a fallback is the point — it lets the caller keep
 * its own wording. What it must never do is hand back the backend's `message`
 * or `detail` prose, which is how internal phrasing reaches users.
 */
export function mappedMessageForCode(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data as ApiError | undefined;

  const code =
    typeof data?.detail === 'string'
      ? data.detail
      : typeof data?.detail === 'object'
        ? data.detail.code
        : undefined;

  return (code && ERROR_MESSAGES[code]) || null;
}

/**
 * Handle API errors with consistent error processing
 */
export function handleApiError(error: AxiosError, context = 'Operation'): string {
  const message = getErrorMessage(error, `${context} failed`);
  console.error(`API Error [${context}]:`, error);
  return message;
}

/**
 * Check if error is a specific auth error code
 */
export function isAuthError(error: AxiosError, errorCode: string): boolean {
  const responseData = error.response?.data as ApiError | undefined;

  if (typeof responseData?.detail === 'string') {
    return responseData.detail === errorCode;
  }

  if (typeof responseData?.detail === 'object' && responseData.detail.code) {
    return responseData.detail.code === errorCode;
  }

  return false;
}

/**
 * Check if error is a validation error (422)
 */
export function isValidationError(error: AxiosError): boolean {
  return error.response?.status === 422;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: AxiosError): boolean {
  return !error.response;
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthenticationError(error: AxiosError): boolean {
  return error.response?.status === 401;
}

/**
 * Check if error is an authorization error (403)
 */
export function isAuthorizationError(error: AxiosError): boolean {
  return error.response?.status === 403;
}
